/* TP · shared analytics base (C4)
 *
 * Why this exists:
 *   GA4 arrives through Shopify's web pixel and is injected asynchronously, so
 *   window.gtag can still be missing when an early event fires. Every previous
 *   call site did `if (typeof gtag === 'function')` and silently dropped the
 *   event otherwise — measured during C3: the popup's own events were lost that
 *   way, and the cart drawer's tp_drawer_open (fired the instant the drawer
 *   opens) is the most exposed of all.
 *
 *   So: queue, then replay once gtag shows up. Bounded — if it never loads we
 *   stop waiting rather than hold the queue forever.
 *
 * Contract: docs/specs/C4-event-contract-v1.md
 *   - zero PII: never pass emails, names, free text, or Shopify error strings
 *   - low cardinality: parameter values are closed enumerations
 *   - never throws: analytics must not be able to break add-to-cart or checkout
 */
window.TPTrack = (function () {
  var queue = [];
  var timer = 0;
  var WAIT_MS = 500;
  var GIVE_UP_MS = 20000;

  function drain() {
    if (typeof window.gtag !== 'function') return false;
    while (queue.length) {
      try { window.gtag.apply(null, queue.shift()); } catch (e) { /* never throw */ }
    }
    return true;
  }

  function push(args) {
    queue.push(args);
    if (drain() || timer) return;
    timer = setInterval(function () {
      if (drain()) { clearInterval(timer); timer = 0; }
    }, WAIT_MS);
    setTimeout(function () { if (timer) { clearInterval(timer); timer = 0; } }, GIVE_UP_MS);
  }

  // page_type comes from Liquid's request.page_type, stamped on <body> so every
  // call site gets it without threading it through.
  function pageType() {
    try {
      var b = document.body;
      return (b && b.getAttribute('data-tp-page-type')) || 'unknown';
    } catch (e) { return 'unknown'; }
  }

  function event(name, params) {
    try {
      var p = {};
      if (params) { for (var k in params) { if (params[k] != null) p[k] = params[k]; } }
      if (p.page_type == null) p.page_type = pageType();
      push(['event', name, p]);
    } catch (e) { /* never throw */ }
  }

  // Fires `name` at most once per page load. Used by the funnel events whose
  // whole point is a clean denominator (config_view / config_start, and each
  // configurator step — re-picking a size must not inflate the step count).
  var seen = {};
  function once(key, name, params) {
    try {
      if (seen[key]) return;
      seen[key] = 1;
      event(name, params);
    } catch (e) { /* never throw */ }
  }

  // ── shelf → PDP ─────────────────────────────────────────────────────────────
  // The product card (tp-home-card-v2) is shared by the home, collection, search
  // and guide shelves and had no tracking at all, so "browsed but never opened a
  // cake" was invisible. One delegated listener covers every shelf instead of
  // editing five consumers; delegation also survives the theme re-rendering grids.
  var LIST_BY_PAGE = { index: 'home', collection: 'collection', search: 'search', page: 'guide', article: 'guide' };
  // The theme has FOUR product-card shapes, not one. tp-home-card-v2 wraps its links in
  // `.tp-card`; the other three ARE the link. Keying only off `.tp-card` silently missed
  // the C2 birthday/order shelves entirely (measured on live 2026-08-15) — which the C4
  // task card explicitly required. Matching must stay this specific: a bare
  // `a[href*="/products/"]` would also catch cart-drawer line-item links.
  // 列表页 v3(tp-shelf,TP-SYNC-20261011-COLLECTION-REDESIGN-01)的卡片包在 .tp-shelf__item / .tp-shelf__tc 里
  var CARD_LINKS = '.tp-c2__card, .tp-seo-hub__product-card, .tp-article__card, .tp-shelf__item, .tp-shelf__tc';
  function bindShelf() {
    try {
      document.addEventListener('click', function (e) {
        try {
          var t = e.target;
          if (!t || !t.closest) return;
          // Anchor on the CARD, not on a link class: the shelves render several
          // link variants (media / title / add) and the class list differs per
          // shelf, so keying off link classes silently misses most cards —
          // verified against the live DOM rather than read off one snippet.
          var link = t.closest('a[href*="/products/"]');
          if (!link) return;
          var card = link.closest('.tp-card') || link.closest(CARD_LINKS);
          if (!card) return;
          var pos = 0;
          if (card && card.parentElement) {
            var sibs = card.parentElement.children;
            for (var i = 0; i < sibs.length; i++) { if (sibs[i] === card) { pos = i + 1; break; } }
          }
          var pt = pageType();
          event('tp_shelf_click', {
            funnel_stage: 'shelf',
            list_name: LIST_BY_PAGE[pt] || 'other',
            position: pos
          });
        } catch (err) { /* never throw */ }
      }, true);
    } catch (e) { /* never throw */ }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindShelf);
  else bindShelf();

  // ── dietary search redirect → keep the site-search count ───────────────────
  // snippets/tp-search-intent.liquid sends "0 sugar" / "keto" style searches
  // straight to the collection before /search renders, so GA4's enhanced
  // measurement never sees the /search?q= page view. The redirect stashes the
  // term in sessionStorage; here, on the target collection, we send the same
  // view_search_results GA4 would have sent. The term is NOT put in the
  // collection URL: Shopify keeps ?q= in canonical_url (measured on theme dev
  // 2026-10-06), which would mint duplicate collection URLs.
  // search_term is free text — the one exception to the contract, and only the
  // exact value GA4 already collects on every /search visit.
  try {
    var raw = sessionStorage.getItem('tp_search_redirect');
    if (raw) {
      sessionStorage.removeItem('tp_search_redirect');
      var s = JSON.parse(raw);
      if (s && s.p === location.pathname && Date.now() - s.t < 60000 && s.q) {
        event('view_search_results', { search_term: String(s.q).slice(0, 100), detail: 'diet_redirect' });
      }
    }
  } catch (e) { /* never throw */ }

  // ── clicks that leave the page (手机第二批 tp_entry_click / tp_diet_set) ──────
  // A click on a home tile or the bottom tab bar navigates straight away. If gtag
  // is already on the page the hit goes out now (gtag.js sends with beacon, so the
  // navigation doesn't cut it off). If gtag hasn't arrived yet, the queue above
  // would die with the page — so stash it and replay on the next page, keeping the
  // page_type of the page where the click happened. Bounded: last 5, 60 seconds.
  function nav(name, params) {
    try {
      if (typeof window.gtag === 'function') { event(name, params); return; }
      var q = [];
      try { q = JSON.parse(sessionStorage.getItem('tp_track_later') || '[]') || []; } catch (e) { q = []; }
      q.push({ n: name, p: params || {}, t: Date.now(), pt: pageType() });
      sessionStorage.setItem('tp_track_later', JSON.stringify(q.slice(-5)));
    } catch (e) { event(name, params); }
  }
  try {
    var later = sessionStorage.getItem('tp_track_later');
    if (later) {
      sessionStorage.removeItem('tp_track_later');
      var lq = JSON.parse(later) || [];
      for (var li = 0; li < lq.length; li++) {
        var x = lq[li];
        if (x && x.n && Date.now() - x.t < 60000) {
          var xp = x.p || {};
          xp.page_type = x.pt || xp.page_type;
          event(x.n, xp);
        }
      }
    }
  } catch (e) { /* never throw */ }

  return { event: event, once: once, nav: nav, pageType: pageType };
})();
